import React, { useState, useEffect } from 'react';
import { Star, Quote } from 'lucide-react';
import { testimonials as mockTestimonials } from '../data/mock';
import ReviewForm from './ReviewForm';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TestimonialsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${API}/reviews`);
        if (response.data.length > 0) {
          setReviews(response.data);
        } else {
          // Use mock testimonials if no real reviews yet
          setReviews(mockTestimonials);
        }
      } catch (error) {
        console.error('Error fetching reviews:', error);
        // Fallback to mock testimonials
        setReviews(mockTestimonials);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  const displayedReviews = reviews.slice(0, 3);

  return (
    <section className="py-24 bg-neutral-50 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-brand font-semibold text-sm tracking-wider uppercase mb-4 block">
            Testimonials
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 mb-6">
            What Our Customers Say
          </h2>
          <p className="text-neutral-600 max-w-2xl mx-auto">
            Don't just take our word for it. Here's what our satisfied customers
            across Lincolnshire have to say about our services.
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {loading ? (
            // Loading skeletons
            [1, 2, 3].map((i) => (
              <div key={i} className="bg-white border border-neutral-200 rounded-2xl p-8 animate-pulse">
                <div className="h-4 bg-neutral-200 rounded w-24 mb-4" />
                <div className="h-20 bg-neutral-200 rounded mb-4" />
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-neutral-200 rounded-full" />
                  <div className="h-4 bg-neutral-200 rounded w-20" />
                </div>
              </div>
            ))
          ) : (
            displayedReviews.map((review, index) => (
              <div
                key={review.id || index}
                className="bg-white border border-neutral-200 rounded-2xl p-8 hover:border-brand/30 hover:shadow-lg transition-all duration-300 relative group"
              >
                {/* Quote Icon */}
                <div className="absolute -top-4 left-8">
                  <div className="w-10 h-10 bg-brand rounded-lg flex items-center justify-center">
                    <Quote className="w-5 h-5 text-white" />
                  </div>
                </div>

                {/* Stars */}
                <div className="flex gap-1 mb-4 pt-4">
                  {[...Array(review.rating || 5)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 text-brand fill-brand"
                    />
                  ))}
                </div>

                {/* Text */}
                <p className="text-neutral-700 leading-relaxed mb-6">
                  "{review.text}"
                </p>

                {/* Author */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-brand/20 rounded-full flex items-center justify-center">
                    <span className="text-brand font-semibold">
                      {review.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="text-neutral-900 font-medium">{review.name}</p>
                    <p className="text-neutral-500 text-sm">{review.location}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Review Form */}
        <div className="max-w-xl mx-auto">
          <ReviewForm />
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
